import React, { useState } from 'react';
import { ColumnsIcon, XIcon, GripVerticalIcon } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
export interface Column {
  id: string;
  label: string;
  visible: boolean;
}
export interface ColumnToggleProps {
  columns: Column[];
  onChange: (columns: Column[]) => void;
}
export function ColumnToggle({
  columns,
  onChange
}: ColumnToggleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const handleToggle = (columnId: string) => {
    const updatedColumns = columns.map(col => col.id === columnId ? {
      ...col,
      visible: !col.visible
    } : col);
    onChange(updatedColumns);
  };
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Set drag image to be semi-transparent
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };
  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  };
  const handleDragLeave = () => {
    setDragOverIndex(null);
  };
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }
    const newColumns = [...columns];
    const draggedItem = newColumns[draggedIndex];
    // Remove dragged item
    newColumns.splice(draggedIndex, 1);
    // Insert at new position
    newColumns.splice(dropIndex, 0, draggedItem);
    onChange(newColumns);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  return <div className="relative" data-id="element-799">
      <Button variant="outline" onClick={() => setIsOpen(!isOpen)} iconLeft={<ColumnsIcon className="w-4 h-4" data-id="element-801" />} data-id="element-800">
        Columns
      </Button>

      {isOpen && <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} data-id="element-802" />
          <div className="absolute right-0 mt-2 w-64 bg-white border border-[#CBD5E1] rounded-lg shadow-lg z-20 p-2" data-id="element-803">
            {/* Header */}
            <div className="flex items-center justify-between mb-2 px-2" data-id="element-804">
              <span className="text-sm font-semibold text-[#0F172A]" data-id="element-805">
                Toggle Columns
              </span>
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-[#F1F5F9] rounded transition-colors" data-id="element-806">
                <XIcon className="w-4 h-4 text-[#94A3B8]" data-id="element-807" />
              </button>
            </div>

            {/* Hint Text */}
            <div className="px-2 pb-2 mb-2 border-b border-[#E2E8F0]" data-id="element-808">
              <p className="text-xs text-[#94A3B8]" data-id="element-809">Drag to reorder columns</p>
            </div>

            {/* Column List */}
            <div className="space-y-1" data-id="element-810">
              {columns.map((column, index) => <div key={column.id} className="relative" data-id="element-811">
                  {/* Drop Indicator - Show above item when dragging over */}
                  {dragOverIndex === index && draggedIndex !== null && draggedIndex !== index && <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#0EA5E9] -mt-0.5 z-10" data-id="element-812" />}

                  <label draggable onDragStart={e => handleDragStart(e, index)} onDragEnd={handleDragEnd} onDragOver={e => handleDragOver(e, index)} onDragLeave={handleDragLeave} onDrop={e => handleDrop(e, index)} className={`flex items-center gap-2 px-2 py-2 hover:bg-[#F8FAFC] rounded-md cursor-grab active:cursor-grabbing transition-colors ${draggedIndex === index ? 'opacity-50' : ''}`} data-id="element-813">
                    {/* Grip Icon */}
                    <GripVerticalIcon className="w-4 h-4 text-[#94A3B8] flex-shrink-0" data-id="element-814" />

                    {/* Checkbox */}
                    <input type="checkbox" checked={column.visible} onChange={() => handleToggle(column.id)} onClick={e => e.stopPropagation()} className="w-[18px] h-[18px] text-[#0EA5E9] border-2 border-[#CBD5E1] rounded focus:ring-[3px] focus:ring-[#0EA5E9] focus:ring-opacity-20" data-id="element-815" />

                    {/* Label */}
                    <span className="text-sm text-[#0F172A] select-none flex-1" data-id="element-816">
                      {column.label}
                    </span>
                  </label>

                  {/* Drop Indicator - Show below last item */}
                  {dragOverIndex === index && draggedIndex !== null && draggedIndex < index && index === columns.length - 1 && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0EA5E9] -mb-0.5 z-10" data-id="element-817" />}
                </div>)}
            </div>
          </div>
        </>}
    </div>;
}